import { Box, CircularProgress, Typography, useTheme } from "@mui/material";
import { ArrowUp, Chart2 } from "iconsax-react";
import { memo, useMemo } from "react";
import { useTranslation } from "react-i18next";

import { formatPersianNumber } from "@/lib/format/persian";
import type { OrderTrendChartProps } from "./OrderTrendChart.types";
import * as styles from "./OrderTrendChart.styles";

export type { OrderTrendChartProps, OrderTrendPoint } from "./OrderTrendChart.types";

export const OrderTrendChart = memo(function OrderTrendChart({
  data,
  growthPercent,
  isLoading = false,
}: OrderTrendChartProps) {
  const { t } = useTranslation("home");
  const theme = useTheme();

  const maxValue = useMemo(() => Math.max(...data.map((point) => point.value), 1), [data]);
  const hasData = data.some((point) => point.value > 0);
  const growthLabel =
    growthPercent !== undefined
      ? t("dashboard.trend.growth", { percent: formatPersianNumber(Math.abs(growthPercent)) })
      : undefined;

  return (
    <Box sx={styles.card}>
      <Box sx={styles.header}>
        <Box sx={styles.titleRow}>
          <Chart2 size={19} variant="Bold" color={theme.palette.primary.main} />
          <Typography variant="h6" sx={styles.title}>
            {t("dashboard.trend.title")}
          </Typography>
        </Box>
        {hasData && growthLabel ? (
          <Box sx={styles.trendChip}>
            <ArrowUp size={15} variant="Linear" color={theme.palette.success.main} />
            <Typography component="span" variant="caption" fontWeight={600}>
              {growthLabel}
            </Typography>
          </Box>
        ) : null}
      </Box>

      {isLoading ? (
        <Box sx={styles.emptyState}>
          <CircularProgress size={32} />
        </Box>
      ) : !hasData ? (
        <Box sx={styles.emptyBox(theme)}>
          <Chart2 size={46} variant="Linear" color={theme.palette.text.disabled} />
          <Typography variant="body2" fontWeight={600} color="text.secondary">
            {t("dashboard.trend.emptyTitle")}
          </Typography>
          <Typography variant="caption" color="text.disabled" sx={styles.emptyDescription}>
            {t("dashboard.trend.emptyDescription")}
          </Typography>
        </Box>
      ) : (
        <Box sx={styles.chartWrap}>
          {data.map((point) => {
            const heightPercent = `${Math.round((point.value / maxValue) * 100)}%`;
            const fill = point.highlight
              ? theme.palette.primary.main
              : theme.palette.primary.light;

            return (
              <Box key={point.label} sx={styles.barColumn}>
                <Box
                  sx={styles.bar(heightPercent, fill)}
                  aria-label={`${point.label}: ${formatPersianNumber(point.value)}`}
                />
                <Typography variant="caption" color="text.secondary">
                  {point.label}
                </Typography>
              </Box>
            );
          })}
        </Box>
      )}
    </Box>
  );
});
